<template>
  <div class="py-6 space-y-6 h-full flex flex-col">
    <!-- === HEADER === -->
    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-6 relative"
    >
      <div class="flex items-start justify-between sm:block flex-1">
        <div>
          <h1 class="text-3xl font-bold title-color shimmerTarget">
            {{ localRiderData?.name || '-' }}
          </h1>
          <p class="text-gray-600 shimmerTarget">
            Email :
            <span class="font-semibold text-gray-800">
              {{ localRiderData?.email || '-' }}
            </span>
          </p>
          <p v-if="localRiderData?.phoneNumber" class="text-gray-600 text-sm shimmerTarget">
            Téléphone :
            <span class="font-semibold text-gray-800">
              {{ localRiderData?.phoneNumber }}
            </span>
          </p>
        </div>
      </div>

      <div class="flex items-center gap-4">
        <div class="text-right shimmerTarget space-y-1">
          <div class="text-gray-700 text-lg">
            Note :
            <span class="font-semibold text-gray-900">
              {{ localRiderData?.note?.toFixed(1) || '-' }}
            </span>
          </div>
          <div class="flex flex-col items-end gap-2">
            <div class="flex items-center space-x-2">
              <div
                :class="[
                  'w-3 h-3 rounded-full',
                  localRiderData?.status === 'online' ? 'bg-green-500' : 'bg-gray-400',
                ]"
              ></div>
              <span class="text-sm font-medium" :class="localRiderData?.status === 'online' ? 'text-green-700' : 'text-gray-600'">
                {{ localRiderData?.status === 'online' ? 'En ligne' : 'Hors ligne' }}
              </span>
            </div>
            <div v-if="isSuspended" 
              class="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 flex items-center gap-1"
            >
              <i class="pi pi-ban text-xs"></i>
              Compte suspendu
            </div>
            <div v-if="localRiderData?.kycStatus" 
              :style="{ backgroundColor: statusColor.background, color: statusColor.color }"
              class="px-3 py-1 rounded-full text-xs font-bold"
            >
              KYC: {{ statusLabel }}
            </div>
          </div>
        </div>

        <!-- Close Button -->
        <button
          @click="$emit('close')"
          class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors self-start"
          title="Fermer"
        >
          <i class="pi pi-times text-lg"></i>
        </button>
      </div>
    </div>

    <!-- === STATISTIQUES D'UTILISATION === -->
    <div
      class="rounded-2xl border border-gray-200 bg-white p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
    >
      <div
        class="flex flex-col items-center p-4 bg-orange-50 rounded-xl border border-orange-100 shimmerTarget"
      >
        <span class="text-gray-500">Solde disponible</span>
        <span class="text-xl font-bold text-orange-600">
          {{
            localRiderData?.balance !== null && localRiderData?.balance !== undefined 
              ? formatNumber(localRiderData.balance, { suffix: ' FCFA' })
              : '—'
          }}
        </span>
      </div>

      <div
        class="flex flex-col items-center p-4 bg-gray-50 rounded-xl border border-gray-100 shimmerTarget"
      >
        <span class="text-gray-500">Total livraisons</span>
        <span class="text-xl font-bold text-gray-900">
          {{ localRiderData?.totalOrdersDelivered || 0 }}
        </span>
      </div>

      <div
        class="flex flex-col items-center p-4 bg-gray-50 rounded-xl border border-gray-100 shimmerTarget"
      >
        <span class="text-gray-500">Commandes actives</span>
        <span class="text-xl font-bold text-gray-900">
          {{ localRiderData?.activeOrders || 0 }}
        </span>
      </div>

      <div
        class="flex flex-col items-center p-4 bg-gray-50 rounded-xl border border-gray-100 shimmerTarget"
      >
        <span class="text-gray-500">Total commissions</span>
        <span class="text-xl font-bold text-gray-900">
          {{ formatNumber(localRiderData?.totalCommission || 0, { suffix: ' FCFA' }) }}
        </span>
      </div>
    </div>

    <!-- === ACTIONS === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <i class="pi pi-cog text-gray-500"></i>
        Actions rapides
      </h3>
      <div class="flex flex-wrap gap-3">
        <button
          @click="handleOpenChat"
          class="flex-1 min-w-[150px] px-4 py-3 rounded-xl border-2 border-blue-100 bg-blue-50 text-blue-700 font-bold text-sm hover:bg-blue-100 transition-all flex items-center justify-center gap-2"
        >
          <i class="pi pi-comments"></i>
          Ouvrir une conversation
        </button>
        <button
          @click="showWarningModal = true"
          class="flex-1 min-w-[150px] px-4 py-3 rounded-xl border-2 border-amber-100 bg-amber-50 text-amber-700 font-bold text-sm hover:bg-amber-100 transition-all flex items-center justify-center gap-2"
        >
          <i class="pi pi-exclamation-triangle"></i>
          Donner un avertissement
        </button>
        <button
          @click="showFineModal = true"
          class="flex-1 min-w-[150px] px-4 py-3 rounded-xl border-2 border-red-100 bg-red-50 text-red-700 font-bold text-sm hover:bg-red-100 transition-all flex items-center justify-center gap-2"
        >
          <i class="pi pi-dollar"></i>
          Appliquer une amende
        </button>
        <button
          @click="handleBlockRider"
          :disabled="isSubmitting"
          class="flex-1 min-w-[150px] px-4 py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-2"
          :class="isSuspended ? 'border-success-100 bg-success-50 text-success-700 hover:bg-success-100' : 'border-red-100 bg-red-50 text-red-700 hover:bg-red-100'"
        >
          <i class="pi" :class="isSuspended ? 'pi-check-circle' : 'pi-ban'"></i>
          {{ isSuspended ? 'Débloquer le rider' : 'Bloquer le rider' }}
        </button>
      </div>
    </div>

    <!-- Order / Package History Tabs -->
    <div class="pb-24">
      <!-- Tab switcher -->
      <div class="flex gap-2 mb-4">
        <button
          @click="activeTab = 'orders'"
          :class="[
            'px-5 py-2 rounded-xl text-sm font-bold transition-all',
            activeTab === 'orders'
              ? 'bg-primary-600 text-white shadow-sm'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50',
          ]"
        >
          <i class="pi pi-shopping-bag mr-1.5"></i>
          Commandes
        </button>
        <button
          @click="activeTab = 'packages'"
          :class="[
            'px-5 py-2 rounded-xl text-sm font-bold transition-all',
            activeTab === 'packages'
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50',
          ]"
        >
          <i class="pi pi-box mr-1.5"></i>
          Colis 
        </button>
      </div>

      <!-- Orders tab -->
      <GridList
        v-if="activeTab === 'orders' && (riderData?.riderId || riderData?.id)"
        title="Commandes du livreur"
        dataKey="orders"
        hasSearch
        :cols="orderCols"
        :limit="10"
        :defaultFilters="{ riderId: riderData.riderId || riderData.id }"
        :cardConfig="{
          titleField: 'id',
          titlePrefix: '#',
          titleFormatter: id => {
            if (!id) return '-'
            if (id.length > 20) return `${id.substring(0, 10)}...${id.substring(id.length - 6)}`
            return id
          },
          subtitleField: 'storeName',
          statusField: 'status',
          extraFields: [
            { key: 'orderDate', label: 'Date', formatter: value => Commons.time(value) },
            { key: 'totalPrice', label: 'Total', formatter: value => formatNumber(value, { suffix: ' FCFA' }) },
          ],
        }"
        :gridCols="{ sm: 1, md: 2, lg: 2, xl: 2 }"
      />

      <!-- Packages tab -->
      <GridList
        v-if="activeTab === 'packages' && (riderData?.riderId || riderData?.id)"
        title="Colis du livreur"
        dataKey="packages"
        hasSearch
        :cols="packageCols"
        :limit="10"
        :defaultFilters="{ riderId: riderData.riderId || riderData.id }"
        :cardConfig="{
          titleField: 'reference',
          titlePrefix: '#',
          subtitleField: 'merchantName',
          statusField: 'status',
          extraFields: [
            { key: 'createdAt', label: 'Date', formatter: value => Commons.time(value) },
            { key: 'totalPrice', label: 'Prix', formatter: value => formatNumber(value, { suffix: ' FCFA' }) },
          ],
        }"
        :gridCols="{ sm: 1, md: 2, lg: 2, xl: 2 }"
      />
    </div>


    <!-- Warnings List -->
    <div v-if="riderData?.warnings?.length > 0" class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <i class="pi pi-history text-gray-500"></i>
        Historique des avertissements
      </h3>
      <div class="space-y-3">
        <div v-for="w in riderData.warnings" :key="w.id" class="p-4 rounded-xl border border-amber-100 bg-amber-50/50 flex justify-between items-start gap-4">
          <div>
            <p class="text-sm font-bold text-amber-900 mb-1">{{ w.reason }}</p>
            <p class="text-xs text-amber-700 opacity-75">{{ new Date(w.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) }}</p>
          </div>
          <span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-800">
            {{ w.severity || 'low' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Warning Modal -->
    <Teleport to="body">
      <div v-if="showWarningModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div class="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 class="text-xl font-bold text-gray-900 text-amber-600">Donner un avertissement</h2>
          </div>
          <div class="p-6 space-y-4">
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700">Raison de l'avertissement</label>
              <textarea v-model="warningForm.reason" rows="3" class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none" placeholder="Ex: Retard répété..."></textarea>
            </div>
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700">Sévérité</label>
              <select v-model="warningForm.severity" class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none">
                <option value="low">Faible</option>
                <option value="medium">Moyenne</option>
                <option value="high">Élevée</option>
              </select>
            </div>
            <div class="flex gap-3 pt-4">
              <button @click="showWarningModal = false" class="flex-1 py-3 bg-gray-100 rounded-xl font-bold">Annuler</button>
              <button @click="submitWarning" :disabled="isSubmitting" class="flex-1 py-3 bg-amber-600 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                <i v-if="isSubmitting" class="pi pi-spin pi-spinner"></i>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Fine Modal -->
    <Teleport to="body">
      <div v-if="showFineModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div class="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-xl font-bold text-red-600">Appliquer une amende</h2>
          </div>
          <div class="p-6 space-y-4">
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700">Montant (FCFA)</label>
              <input v-model.number="fineForm.amount" type="number" class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none" placeholder="Ex: 1000">
            </div>
            <div class="space-y-1.5">
              <label class="text-sm font-bold text-gray-700">Motif</label>
              <textarea v-model="fineForm.reason" rows="3" class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none" placeholder="Ex: Comportement inapproprié..."></textarea>
            </div>
            <div class="bg-red-50 p-4 rounded-xl flex items-start gap-3 border border-red-100">
              <i class="pi pi-info-circle text-red-600 mt-1"></i>
              <p class="text-xs text-red-800 leading-relaxed">Cette somme sera immédiatement déduite du solde du rider et ne pourra pas être annulée.</p>
            </div>
            <div class="flex gap-3 pt-4">
              <button @click="showFineModal = false" class="flex-1 py-3 bg-gray-100 rounded-xl font-bold">Annuler</button>
              <button @click="submitFine" :disabled="isSubmitting" class="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                <i v-if="isSubmitting" class="pi pi-spin pi-spinner"></i>
                Appliquer
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import GridList from '../others/GridList.vue'
import Commons from '@/shared/utils/commons'
import statuses from '../orders/statuses.json'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import { buildApiUrl, ADMIN_ENDPOINTS } from '../../configs/api.js'
import { initChatWidget, CHAT_CONFIG } from '@/features/chat/chatInit.js'
import { openChatWithRider } from '@/features/chat/chatUtils.js'

const props = defineProps({
  riderData: {
    type: Object,
    default: () => ({}),
  },
})

// Emits
const emit = defineEmits(['open-chat', 'toggle-status', 'refresh', 'close', 'update:riderData'])

// Données locales réactives du rider pour mise à jour immédiate de l'interface
const localRiderData = ref({ ...(props.riderData || {}) })

watch(
  () => props.riderData,
  newVal => {
    if (newVal) {
      localRiderData.value = { ...newVal }
    }
  },
  { deep: true, immediate: true }
)

// Colonnes commandes du rider
const orderCols = [
  {
    id: 'orderDate',
    name: 'Date',
    value(el) { return Commons.time(el?.orderDate || el?.createdAt) },
  },
  {
    id: 'status',
    name: 'Statut',
    filter: {
      key: 'status',
      default: '',
      options: [
        { name: 'Tout', value: '' },
        { name: 'Livrée', value: 'delivered' },
        { name: 'Annulée', value: 'cancelled' },
        { name: 'En cours', value: 'accepted' },
      ],
    },
    value(el) {
      const status = statuses[el?.status] || { name: el?.status || 'Inconnu', background: '#6B7280', color: '#FFFFFF' }
      return `<div style="background-color: ${status.background}; color: ${status.color}" class="inline-block px-3 py-0.5 rounded-full font-semibold text-xs">${status.name}</div>`
    },
  },
  {
    name: 'Restaurant',
    value(el) { return el?.storeName || el?.restaurantName || '-' },
  },
  {
    name: 'Total',
    value(el) { return Commons.formatNumber(el?.totalPrice || 0, { suffix: ' FCFA' }) },
  },
]

// Colonnes colis du rider
const packageCols = [
  {
    id: 'createdAt',
    name: 'Date',
    value(el) { return Commons.time(el?.createdAt || el?.orderDate) },
  },
  {
    id: 'status',
    name: 'Statut',
    filter: {
      key: 'status',
      default: '',
      options: [
        { name: 'Tout', value: '' },
        { name: 'Livré', value: 'delivered' },
        { name: 'En transit', value: 'in_transit' },
        { name: 'Récupéré', value: 'picked_up' },
        { name: 'Annulé', value: 'cancelled' },
      ],
    },
    value(el) {
      const colorMap = {
        delivered: { bg: '#DEF7EC', color: '#03543F', label: 'Livré' },
        picked_up: { bg: '#E1EFFE', color: '#1E429F', label: 'Récupéré' },
        in_transit: { bg: '#FEF3C7', color: '#92400E', label: 'En transit' },
        confirmed: { bg: '#EDE9FE', color: '#5B21B6', label: 'Confirmé' },
        cancelled: { bg: '#FDE8E8', color: '#9B1C1C', label: 'Annulé' },
      }
      const s = el?.status || 'pending'
      const c = colorMap[s] || { bg: '#E5E7EB', color: '#374151', label: s }
      return `<div style="background-color: ${c.bg}; color: ${c.color}" class="inline-block px-3 py-0.5 rounded-full font-semibold text-xs">${c.label}</div>`
    },
  },
  {
    name: 'Marchand',
    value(el) { return el?.merchantName || el?.senderName || '-' },
  },
  {
    name: 'Référence',
    value(el) {
      const ref = el?.reference || el?.id || '-'
      return ref.length > 14 ? ref.substring(0, 12) + '…' : ref
    },
  },
  {
    name: 'Prix',
    value(el) { return Commons.formatNumber(el?.totalPrice || el?.price || 0, { suffix: ' FCFA' }) },
  },
]

// Active history tab: 'orders' | 'packages'
const activeTab = ref('orders')


// Identifiants et informations calculées
const targetRiderId = computed(() => localRiderData.value?.riderId || localRiderData.value?.metadata?.riderId || localRiderData.value?.id || '')
const targetUserId = computed(() => localRiderData.value?.userId || localRiderData.value?.ownerId || localRiderData.value?.id || targetRiderId.value)
const targetRiderName = computed(() => (localRiderData.value?.name || `${localRiderData.value?.firstName || ''} ${localRiderData.value?.lastName || ''}`).trim() || 'Livreur')

const isSuspended = computed(() => {
  if (
    localRiderData.value?.metadata?.isSuspended === false ||
    localRiderData.value?.metadata?.suspended === false ||
    localRiderData.value?.isSuspended === false
  ) {
    return false
  }
  return Boolean(
    localRiderData.value?.metadata?.isSuspended ||
    localRiderData.value?.metadata?.suspended ||
    localRiderData.value?.isSuspended ||
    localRiderData.value?.accountStatus === 'suspended'
  )
})

const statusColor = computed(() => {
  const status = localRiderData.value?.kycStatus || 'pending'
  const maps = {
    verified: { background: '#DEF7EC', color: '#03543F' },
    rejected: { background: '#FDE8E8', color: '#9B1C1C' },
    pending: { background: '#E5E7EB', color: '#374151' },
    submitted: { background: '#E1EFFE', color: '#1E429F' },
  }
  return maps[status] || maps.pending
})

const statusLabel = computed(() => {
  const status = localRiderData.value?.kycStatus || 'pending'
  const labels = {
    verified: 'Vérifié',
    rejected: 'Rejeté',
    pending: 'En attente',
    submitted: 'Soumis',
  }
  return labels[status] || status
})

const showWarningModal = ref(false)
const showFineModal = ref(false)
const isSubmitting = ref(false)

const warningForm = ref({
  reason: '',
  severity: 'low',
})

const fineForm = ref({
  amount: null,
  reason: '',
})

function formatNumber(...args) {
  return Commons.formatNumber(...args)
}

function handleOpenChat() {
  emit('open-chat')
  openChatWithRider(props.riderData)
}

// 1. Envoyer un message dans le chat XenoTalk
async function sendChatMessage(text) {
  try {
    await initChatWidget()
    const rId = targetRiderId.value
    const rName = targetRiderName.value
    const adminId = CHAT_CONFIG.adminUserId || 'admin-1'

    if (window.XenoTalk && typeof window.XenoTalk.openConversation === 'function') {
      const conv = await window.XenoTalk.openConversation({
        participant1Id: adminId,
        participant1Role: 'admin',
        participant1Name: 'Support Backoffice',
        participant1NotifService: 'rapidoo-admin',
        participant2Id: rId,
        participant2Role: 'rider',
        participant2Name: rName,
        participant2NotifService: 'rapidoo-rider',
        type: 'support',
        referenceType: 'admin',
        referenceId: `v3-admin-rider-${rId}`,
      })

      const convId = conv?._id || conv?.id
      if (convId) {
        await window.XenoTalk.setConversation(convId)
        if (typeof window.XenoTalk.sendMessage === 'function') {
          await window.XenoTalk.sendMessage(text)
        }
      }
    }
  } catch (error) {
    console.error('Erreur envoi chat XenoTalk:', error)
  }
}

// 2. Enregistrer une notification en base de données
async function sendDbNotification({ title, message, data = {} }) {
  try {
    const url = buildApiUrl(ADMIN_ENDPOINTS.ADMIN_NOTIFICATIONS_SEND)
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: crypto.randomUUID(),
        title: title || '',
        message: message || '',
        type: 'system',
        audience: 'user',
        userId: targetUserId.value,
        target: 'user',
        data: {
          targetRank: null,
          zone: [],
          priority: data.priority || 'high',
          status: 'sent',
          scheduledAt: null,
          createdBy: 'admin',
          riderId: targetRiderId.value,
          ...data,
        },
      }),
    })
  } catch (error) {
    console.error('Erreur sauvegarde notification BDD:', error)
  }
}

// 3. Envoyer une notification Push FCM / OneSignal
async function sendPushNotification({ title, content }) {
  try {
    const url = 'https://api.monrapidoo.com/notification/send?group=ultraxeno'
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: targetUserId.value,
        title: title || '',
        content: content || '',
        service: 'rapidoo-rider',
      }),
    })
  } catch (error) {
    console.error('Erreur envoi push notification:', error)
  }
}

// Flow complet de notification pour le rider (Chat + BDD + Push)
async function triggerRiderNotificationFlow({ title, message, chatMessage, data = {} }) {
  await Promise.allSettled([
    sendChatMessage(chatMessage || message),
    sendDbNotification({ title, message, data }),
    sendPushNotification({ title, content: message }),
  ])
}

// Action : Donner un avertissement
async function submitWarning() {
  if (!warningForm.value.reason) return
  isSubmitting.value = true
  try {
    const reason = warningForm.value.reason
    const severity = warningForm.value.severity || 'low'
    const severityLabel = severity === 'high' ? 'Élevée' : severity === 'medium' ? 'Moyenne' : 'Faible'

    const title = `Avertissement (${severityLabel})`
    const message = `Avertissement de l'administration : ${reason}`
    const chatMsg = `⚠️ AVERTISSEMENT (${severityLabel.toUpperCase()})\n\nMotif : ${reason}\n\nMerci de vous conformer aux règles de la plateforme Rapidoo.`

    // 1. Déclencher les 3 canaux de notification
    await triggerRiderNotificationFlow({
      title,
      message,
      chatMessage: chatMsg,
      data: { severity, type: 'warning' },
    })

    // 2. Enregistrement optionnel backend
    try {
      const url = buildApiUrl(ADMIN_ENDPOINTS.ADMIN_RIDER_WARNINGS, {
        pathParams: { riderId: targetRiderId.value },
      })
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, severity }),
      })
    } catch (e) {
      console.warn('Backend warning log error (ignored):', e)
    }

    showWarningModal.value = false
    
    // Mise à jour locale immédiate
    const newWarning = {
      id: `w_${Date.now()}`,
      reason,
      severity,
      date: new Date().toISOString(),
    }
    localRiderData.value.warnings = [newWarning, ...(localRiderData.value?.warnings || [])]

    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: 'Avertissement envoyé via Chat, Push et Notification',
    })
    Events.dispatch('RIDER_UPDATED')
    emit('update:riderData', { ...localRiderData.value })
    emit('refresh')
    warningForm.value = { reason: '', severity: 'low' }
  } catch (e) {
    console.error(e)
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Impossible d\'envoyer l\'avertissement',
    })
  } finally {
    isSubmitting.value = false
  }
}

// Action : Appliquer une amende
async function submitFine() {
  if (!fineForm.value.amount || !fineForm.value.reason) return
  isSubmitting.value = true
  try {
    const amount = Number(fineForm.value.amount)
    const reason = fineForm.value.reason

    const title = 'Amende appliquée'
    const formattedAmount = Commons.formatNumber(amount, { suffix: ' FCFA' })
    const message = `Une amende de ${formattedAmount} vous a été appliquée. Motif : ${reason}`
    const chatMsg = `🛑 AMENDE APPLIQUÉE\n\nMontant : ${formattedAmount}\nMotif : ${reason}\n\nCette somme a été déduite de votre solde Rapidoo.`

    // 1. Flow de notification (Chat + BDD + Push)
    await triggerRiderNotificationFlow({
      title,
      message,
      chatMessage: chatMsg,
      data: { amount, reason, type: 'fine' },
    })

    // 2. Requête de débit du portefeuille (walletDebit via withdrawalRequested)
    const debitUrl = 'https://api.monrapidoo.com/queue/broadcast/data/rapidoo-transactions/withdrawalRequested?group=commons'
    const debitPayload = {
      id: `trx_wallet_debit_${Date.now()}`,
      requestId: `req_wallet_debit_${Date.now()}`,
      type: 'walletDebit',
      src: {
        wallet: `wallet_${targetRiderId.value}`,
        service: 'rapidoo',
        processor: 'rapidoWallet',
        currency: 'XOF',
        amount: amount,
        country: 'any',
      },
      dst: {
        service: 'rapidoo',
        currency: 'XOF',
        amount: amount,
        provider: 'fedapay',
        country: 'BJ',
        firstName: localRiderData.value?.firstName || 'Rider',
        lastName: localRiderData.value?.lastName || 'Rider',
        phoneNumber: '',
      },
      metadata: {
        source: 'rider',
        ownerId: targetUserId.value,
        riderId: targetRiderId.value,
        wallet: `wallet_${targetRiderId.value}`,
        narration: `Amende au rider : ${reason}`,
      },
    }

    await fetch(debitUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(debitPayload),
    })

    // 3. Enregistrement optionnel backend
    try {
      const fineApiUrl = buildApiUrl(ADMIN_ENDPOINTS.ADMIN_RIDER_FINES, {
        pathParams: { riderId: targetRiderId.value },
      })
      await fetch(fineApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason }),
      })
    } catch (e) {
      console.warn('Backend fine endpoint error (ignored):', e)
    }

    showFineModal.value = false

    // Mise à jour locale immédiate du solde
    localRiderData.value.balance = (localRiderData.value?.balance || 0) - amount

    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: `Amende de ${formattedAmount} appliquée et notifiée`,
    })
    Events.dispatch('RIDER_UPDATED')
    emit('update:riderData', { ...localRiderData.value })
    emit('refresh')
    fineForm.value = { amount: null, reason: '' }
  } catch (e) {
    console.error(e)
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Impossible d\'appliquer l\'amende',
    })
  } finally {
    isSubmitting.value = false
  }
}

// Action : Suspendre / Débloquer le rider
async function handleBlockRider() {
  const currentlySuspended = isSuspended.value
  const newSuspendedState = !currentlySuspended
  isSubmitting.value = true

  try {
    const rId = targetRiderId.value
    const updateUrl = `https://api.monrapidoo.com/rider/update/${rId}?group=ultraxeno`

    const updatedMetadata = {
      ...(localRiderData.value?.metadata || {}),
      isSuspended: newSuspendedState,
      suspended: newSuspendedState,
      suspendedAt: newSuspendedState ? new Date().toISOString() : null,
      suspendedReason: newSuspendedState ? 'Compte suspendu par l\'administration' : null,
    }

    const payload = {
      metadata: updatedMetadata,
      accountStatus: newSuspendedState ? 'suspended' : 'active',
    }

    await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    // Gestion du statut en ligne/hors ligne :
    // S'il était en ligne lors de la suspension, on bascule son statut vers hors ligne.
    // S'il était déjà hors ligne, on ne fait rien.
    // Lors du déblocage, on ne le remet PAS en ligne automatiquement (il reste hors ligne jusqu'à sa connexion).
    if (newSuspendedState) {
      if (localRiderData.value?.status === 'online') {
        try {
          const toggleUrl = `https://api.monrapidoo.com/rider/toggle/status/${rId}?group=ultraxeno`
          await fetch(toggleUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          })
          localRiderData.value.status = 'offline'
        } catch (toggleErr) {
          console.warn('Erreur lors du passage hors ligne du rider:', toggleErr)
        }
      }
    }

    // Mise à jour locale immédiate
    localRiderData.value.metadata = updatedMetadata
    localRiderData.value.accountStatus = newSuspendedState ? 'suspended' : 'active'

    // Flow complet de notification (Chat + BDD + Push)
    const title = newSuspendedState ? 'Compte Suspendu' : 'Compte Réactivé'
    const message = newSuspendedState
      ? 'Votre compte livreur a été temporairement suspendu par l\'administration. Veuillez contacter le support pour plus d\'informations.'
      : 'Votre compte livreur a été réactivé avec succès. Vous pouvez désormais vous connecter et recevoir des courses.'
    const chatMsg = newSuspendedState
      ? '⛔ SUSPENSION DE COMPTE\n\nVotre compte livreur a été suspendu par l\'administration. Pour toute assistance ou réclamation, veuillez répondre directement à ce message.'
      : '✅ COMPTE RÉACTIVÉ\n\nVotre compte livreur est de nouveau actif. Vous pouvez reprendre vos livraisons dès maintenant.'

    await triggerRiderNotificationFlow({
      title,
      message,
      chatMessage: chatMsg,
      data: { isSuspended: newSuspendedState, type: 'suspension' },
    })

    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: newSuspendedState ? 'Rider suspendu et notifié' : 'Rider débloqué et notifié',
    })
    Events.dispatch('RIDER_UPDATED')
    emit('update:riderData', { ...localRiderData.value })
    emit('refresh')
    if(newSuspendedState){
      emit('toggle-status', { id: rId, status: newSuspendedState ? 'suspended' : 'active' })
    }
  } catch (e) {
    console.error(e)
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: 'Impossible de modifier le statut du rider',
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.title-color {
  color: #1f2937;
}
</style>
